<template>
  <section class="aq">
    <div class="aq__head">
      <span v-if="horizon" class="aq__eyebrow" :class="'aq__eyebrow--' + accent">{{ horizon }}</span>
      <div class="aq__headrow">
        <h2 class="aq__title">{{ title }}</h2>
        <span class="aq__count" :class="{ 'aq__count--zero': !displayCount }">{{ displayCount }}</span>
      </div>
    </div>

    <div v-if="items && items.length" class="aq__list">
      <button
        v-for="i in items"
        :key="i.id"
        type="button"
        class="aq__row"
        @click="$emit('open-inquiry', i.id)"
      >
        <span class="aq__dot" :class="'aq__dot--' + accent"></span>
        <span class="aq__main">
          <span class="aq__top">
            <span class="aq__name">{{ i.name || "Sin nombre" }}</span>
            <span v-if="i.leadTier" class="aq__lead" :class="'aq__lead--' + i.leadTier">
              {{ tierLabel(i.leadTier) }}<template v-if="i.leadScore != null"> · {{ i.leadScore }}</template>
            </span>
          </span>
          <span class="aq__prop">{{ i.property ? i.property.fullAddress : (i.topic || "Consulta general") }}</span>
          <span v-if="i.topic || i.message" class="aq__msg">{{ truncate(i.topic || i.message) }}</span>
        </span>
        <span class="aq__actions">
          <span class="aq__wait">{{ waitLabel(i.createdAtTime || i.createdAt) }}</span>
          <span class="aq__btns">
            <span class="aq__btn aq__btn--primary">Responder</span>
            <span
              v-if="i.visitorId"
              class="aq__btn"
              role="button"
              @click.stop="$emit('open-journey', i.visitorId)"
            >Recorrido</span>
          </span>
        </span>
      </button>
    </div>

    <div v-else class="aq__empty">{{ empty }}</div>
  </section>
</template>

<script>
const TIER_LABELS = { hot: "Caliente", warm: "Tibio", cold: "Frío" };

export default {
  props: {
    title: { type: String, required: true },
    horizon: { type: String, default: "" },   // urgency framing: Ahora | Hoy | Esta semana
    count: { type: Number, default: null },    // override the badge (else uses items.length)
    items: { type: Array, default: () => [] },
    empty: { type: String, default: "Nada por ahora." },
    accent: { type: String, default: "brand" } // brand | hot | cold
  },

  computed: {
    displayCount() {
      return this.count != null ? this.count : (this.items ? this.items.length : 0);
    }
  },

  methods: {
    tierLabel(t) {
      return TIER_LABELS[t] || t;
    },
    truncate(s, n = 90) {
      if (!s) return "";
      return s.length > n ? s.slice(0, n) + "…" : s;
    },
    // relative "waiting" label — coarse on purpose (no false-precision SLA)
    waitLabel(ts) {
      if (!ts) return "";
      const then = new Date(ts).getTime();
      if (isNaN(then)) return "";
      const mins = Math.floor((Date.now() - then) / 60000);
      if (mins < 1) return "recién";
      if (mins < 60) return `hace ${mins} min`;
      const hours = Math.floor(mins / 60);
      if (hours < 24) return `hace ${hours} h`;
      const days = Math.floor(hours / 24);
      return `hace ${days} d`;
    }
  }
};
</script>

<style scoped>
.aq { margin-bottom: 22px; }
.aq__head { margin-bottom: 12px; }
.aq__eyebrow { font-size: 10px; font-weight: 700; letter-spacing: 0.14em; text-transform: uppercase; color: #a39a8c; }
.aq__eyebrow--brand { color: #b52118; }
.aq__eyebrow--hot { color: #c2761f; }
.aq__eyebrow--cold { color: #5a6b7a; }
.aq__headrow { display: flex; align-items: center; gap: 10px; margin-top: 4px; }
.aq__title { font-size: 17px; font-weight: 700; color: #1a1814; margin: 0; }
.aq__count { font-size: 12px; font-weight: 700; color: #1a1814; background: #f1ece3; border-radius: 999px; padding: 2px 9px; min-width: 24px; text-align: center; }
.aq__count--zero { color: #b8b0a2; background: #f4f0e8; }

.aq__list { display: flex; flex-direction: column; gap: 8px; }
.aq__row {
  display: flex; align-items: flex-start; gap: 12px; width: 100%; text-align: left;
  background: #fff; border: 1px solid #ece7de; border-radius: 12px; padding: 13px 15px;
  cursor: pointer; font: inherit; color: inherit;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
}
.aq__row:hover { border-color: #dcd4c6; box-shadow: 0 8px 24px -16px rgba(40, 33, 22, 0.18); transform: translateY(-1px); }

.aq__dot { flex: none; width: 8px; height: 8px; border-radius: 50%; margin-top: 6px; background: #a39a8c; }
.aq__dot--brand { background: #b52118; }
.aq__dot--hot { background: #e0892f; }
.aq__dot--cold { background: #43596b; }

.aq__main { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
.aq__top { display: flex; align-items: baseline; gap: 8px; flex-wrap: wrap; }
.aq__name { font-size: 14.5px; font-weight: 600; color: #1a1814; }
.aq__lead { flex: none; font-size: 9.5px; font-weight: 700; padding: 1px 7px; border-radius: 999px; text-transform: uppercase; letter-spacing: .03em; }
.aq__lead--hot { background: #fdecea; color: #b52118; }
.aq__lead--warm { background: #fdeecf; color: #8a5a00; }
.aq__lead--cold { background: #eef1f4; color: #6a727b; }
.aq__wait { flex: none; font-size: 11.5px; color: #a39a8c; }
.aq__prop { font-size: 12.5px; font-weight: 600; color: #56504a; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.aq__msg { font-size: 12.5px; color: #756f64; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.aq__actions { flex: none; display: flex; flex-direction: column; align-items: flex-end; gap: 10px; align-self: flex-start; }
.aq__btns { display: flex; align-items: center; gap: 8px; }
.aq__btn {
  display: inline-flex; align-items: center; padding: 7px 13px; border-radius: 8px;
  font-size: 12.5px; font-weight: 600; white-space: nowrap; border: 1px solid #e3ddd2; color: #56504a; background: #fff;
}
.aq__btn--primary { background: #b52118; border-color: #b52118; color: #fff; }
.aq__row:hover .aq__btn--primary { background: #9d1d15; }
.aq__btn:not(.aq__btn--primary):hover { background: #f4f0e8; border-color: #dcd4c6; }

.aq__empty {
  border: 1px dashed #dcd4c6; border-radius: 12px; padding: 26px; text-align: center;
  color: #6a8762; font-size: 13.5px; background: #fbfdfb;
}
</style>
